import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import {
  IconCheck,
  IconChevronDown,
  IconLink,
  IconLinkOff,
  IconAlertCircle,
} from "@tabler/icons-react";

import {
  getMpAuthUrl,
  getMpDisconnect,
  getMpStatus,
} from "../services/finance";
import { formatDate } from "../helpers/format";

const MercadoPagoTab = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Busca status da conexão
  const {
    data: status,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["mp-status"],
    queryFn: getMpStatus,
  });

  // Inicia o OAuth — busca a URL e redireciona
  const { mutate: startConnect, isPending: isPendingConnect } = useMutation({
    mutationFn: getMpAuthUrl,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => {
      toast.error("Não foi possível iniciar a conexão. Tente novamente.");
    },
  });

  // Desconecta
  const { mutate: disconnect, isPending: isPendingDisconnect } = useMutation({
    mutationFn: getMpDisconnect,
    onSuccess: () => {
      setConfirmOpen(false);
      refetchStatus();
      toast.success("Conta Mercado Pago desvinculada.");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(e?.response?.data?.message || "Erro ao desvincular conta.");
    },
  });

  if (isLoadingStatus) {
    return (
      <Grid2 size={12} display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Grid2>
    );
  }

  return (
    <>
      <Grid2 size={12}>
        <Stack
          sx={{ backgroundColor: "secondary.dark", borderRadius: 2, p: 2 }}
          gap={2}
        >
          {/* Cabeçalho */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack>
              <Typography fontSize={{ xs: 16, sm: 18 }} fontWeight={700}>
                Integração Mercado Pago
              </Typography>
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Vincule sua conta para receber pagamentos diretamente dos seus
                alunos.
              </Typography>
            </Stack>

            {/* Badge de status */}
            {status?.connected ? (
              <Chip
                icon={<IconCheck size={14} />}
                label="Conectado"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<IconAlertCircle size={14} />}
                label="Não conectado"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>

          <Divider />

          {/* Conteúdo condicional */}
          {status?.connected ? (
            <ConnectedState
              mpUserId={status.mpUserId}
              connectedAt={status.connectedAt}
              onDisconnect={() => setConfirmOpen(true)}
              isPending={isPendingDisconnect}
            />
          ) : (
            <DisconnectedState
              onConnect={() => startConnect()}
              isPending={isPendingConnect}
            />
          )}

          <Divider />

          {/* FAQ */}
          <FaqSection />
        </Stack>
      </Grid2>

      {/* Modal de confirmação de desconexão */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Desvincular Mercado Pago</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja desvincular sua conta do Mercado Pago? Os
            pagamentos via MP serão interrompidos até que você conecte
            novamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => disconnect()}
            disabled={isPendingDisconnect}
          >
            {isPendingDisconnect ? (
              <CircularProgress size={16} />
            ) : (
              "Desvincular"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── Sub-componentes ───────────────────────────────────────────

const DisconnectedState = ({
  onConnect,
  isPending,
}: {
  onConnect: () => void;
  isPending: boolean;
}) => (
  <Stack gap={2}>
    <Alert severity="warning" icon={<IconAlertCircle size={18} />}>
      Sua conta Mercado Pago ainda não está conectada.
    </Alert>

    {[
      "Aceite Pix, boleto e cartão via Mercado Pago",
      "Receba diretamente na sua conta MP",
      "Gerencie tudo pelo painel do Mercado Pago",
    ].map((item) => (
      <Stack key={item} direction="row" gap={1} alignItems="center">
        <IconCheck size={16} color="green" />
        <Typography fontSize={14}>{item}</Typography>
      </Stack>
    ))}

    <Box>
      <Button
        variant="contained"
        startIcon={
          isPending ? <CircularProgress size={14} /> : <IconLink size={16} />
        }
        onClick={onConnect}
        disabled={isPending}
      >
        Conectar Mercado Pago
      </Button>
    </Box>
  </Stack>
);

const ConnectedState = ({
  mpUserId,
  connectedAt,
  onDisconnect,
  isPending,
}: {
  mpUserId: string | null;
  connectedAt: string | null;
  onDisconnect: () => void;
  isPending: boolean;
}) => (
  <Stack gap={2}>
    <Alert severity="success" icon={<IconCheck size={18} />}>
      Conta conectada com sucesso. Pagamentos via MP estão ativos.
    </Alert>

    <Stack gap={0.5}>
      {mpUserId && (
        <Typography fontSize={13} color="secondary.contrastText">
          ID da conta MP: <strong>{mpUserId}</strong>
        </Typography>
      )}
      {connectedAt && (
        <Typography fontSize={13} color="secondary.contrastText">
          Conectado em: <strong>{formatDate(connectedAt)}</strong>
        </Typography>
      )}
    </Stack>

    <Box>
      <Button
        variant="outlined"
        color="error"
        startIcon={
          isPending ? <CircularProgress size={14} /> : <IconLinkOff size={16} />
        }
        onClick={onDisconnect}
        disabled={isPending}
      >
        Desvincular conta
      </Button>
    </Box>
  </Stack>
);

export default MercadoPagoTab;

// ── FAQ ──────────────────────────────────────────────────────

const faqs = [
  {
    question: "Como faço para conectar minha conta Mercado Pago?",
    answer:
      'Clique no botão "Conectar Mercado Pago" acima. Você será redirecionado para o site do Mercado Pago para autorizar o acesso. Após confirmar, voltará automaticamente para o painel com a conta vinculada.',
  },
  {
    question: "Quais formas de pagamento são aceitas?",
    answer:
      "Com a integração ativa, seus alunos podem pagar via Pix (QR Code e Copia e Cola) e cartão de crédito diretamente na tela de checkout da cobrança.",
  },
  {
    question: "O dinheiro cai direto na minha conta Mercado Pago?",
    answer:
      "Sim. Todo o valor é recebido diretamente na sua conta Mercado Pago, sem intermediários. Você pode transferir para sua conta bancária quando quiser pelo app ou site do Mercado Pago.",
  },
  {
    question: "Preciso ter chave Pix cadastrada no Mercado Pago?",
    answer:
      "Sim. Para que seus alunos consigam pagar via Pix, sua conta Mercado Pago precisa ter pelo menos uma chave Pix cadastrada. Você pode cadastrá-la diretamente no aplicativo do Mercado Pago, em Área Pix > Minhas chaves.",
  },
  {
    question: "O que acontece se eu desvincular a conta?",
    answer:
      "Os pagamentos via Mercado Pago serão interrompidos imediatamente. O sistema volta a utilizar as formas de pagamento anteriores (Inter/Next para Pix, PjBank para cartão). Você pode reconectar a qualquer momento sem perder o histórico.",
  },
  {
    question: "Como funciona a taxa do Mercado Pago?",
    answer:
      "O Mercado Pago cobra uma taxa por transação diretamente no valor recebido. A taxa varia conforme o meio de pagamento e o seu plano no MP. Consulte o site do Mercado Pago para os valores atualizados.",
  },
  {
    question: "Os pagamentos antigos são afetados?",
    answer:
      "Não. Cobranças já pagas ou em andamento não são alteradas. A integração afeta apenas novas cobranças geradas após a conexão ser estabelecida.",
  },
];

const FaqSection = () => (
  <Stack gap={1}>
    <Typography fontSize={14} fontWeight={700}>
      Dúvidas frequentes
    </Typography>
    {faqs.map((faq) => (
      <Accordion
        key={faq.question}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px !important",
          "&:before": { display: "none" },
          "&.Mui-expanded": { borderColor: "primary.main" },
        }}
      >
        <AccordionSummary
          expandIcon={<IconChevronDown size={16} />}
          sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.5 } }}
        >
          <Typography fontSize={13} fontWeight={600}>
            {faq.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography fontSize={13} color="text.secondary">
            {faq.answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </Stack>
);
